import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from "@tanstack/react-table";
import {
  MdEdit,
  MdDelete,
  MdCheckCircle,
  MdCancel,
  MdEmojiEvents,
} from "react-icons/md";
import { Challenge } from "../../types/challenge";
import Card from "../card";
import DataTable from "../common/DataTable";
import LoadingSpinner from "../common/LoadingSpinner";

interface ChallengesTableProps {
  challenges: Challenge[];
  loading: boolean;
  onEdit: (challenge: Challenge) => void;
  onDelete: (challenge: Challenge) => void;
  onView: (challenge: Challenge) => void;
}

const columnHelper = createColumnHelper<Challenge>();

const ChallengesTable: React.FC<ChallengesTableProps> = ({
  challenges,
  loading,
  onEdit,
  onDelete,
  onView,
}) => {
  const columns = useMemo<ColumnDef<Challenge, any>[]>(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: () => "Challenge Name",
        cell: (info) => (
          <div className="text-sm font-medium text-navy-700 dark:text-white">
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor("conducted_at_year", {
        header: () => "Year",
        cell: (info) => (
          <div className="text-sm text-navy-700 dark:text-white">
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor("users_count", {
        header: () => "Users",
        cell: (info) => (
          <div className="text-sm text-navy-700 dark:text-white">
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor("is_active", {
        header: () => "Status",
        cell: (info) => {
          const isActive = info.getValue();
          return isActive ? (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800 dark:bg-green-800 dark:text-green-100">
              Active
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800 dark:bg-gray-800 dark:text-gray-100">
              Inactive
            </span>
          );
        },
      }),
      columnHelper.accessor("created_at", {
        header: () => "Created",
        cell: (info) => {
          const date = new Date(info.getValue());
          return (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {date.toLocaleDateString()}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => "Actions",
        cell: (info) => {
          const challenge = info.row.original;
          return (
            <div className="flex space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(challenge);
                }}
                className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                title="Edit Challenge"
              >
                <MdEdit className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(challenge);
                }}
                className="text-gray-600 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                title="Delete Challenge"
              >
                <MdDelete className="h-5 w-5" />
              </button>
            </div>
          );
        },
      }),
    ],
    [onEdit, onDelete]
  );

  const table = useReactTable({
    data: challenges,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return (
      <Card extra="w-full h-full p-3 md:p-6 sm:overflow-x-auto">
        <LoadingSpinner />
      </Card>
    );
  }

  return (
    <Card extra="w-full h-full p-3 md:p-6 sm:overflow-x-auto">
      <div className="relative flex items-center justify-between pb-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Challenges Table
        </div>
      </div>

      <DataTable
        table={table}
        isEmpty={challenges.length === 0}
        empty={{ icon: MdEmojiEvents, title: "No challenges found" }}
        onRowClick={(_event, row: Challenge) => onView(row)}
        mobile={challenges.map((challenge) => (
          <div
            key={challenge.id}
            className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-navy-900"
            onClick={() => onView(challenge)}
          >
            <div className="mb-2 flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <h3 className="mb-1 font-medium text-navy-700 dark:text-white">
                  {challenge.name}
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Year: {challenge.conducted_at_year}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Users: {challenge.users_count}
                </div>
                <div className="mt-2">
                  {challenge.is_active ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800 dark:bg-green-800 dark:text-green-100">
                      <MdCheckCircle className="mr-1 h-3 w-3" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800 dark:bg-gray-800 dark:text-gray-100">
                      <MdCancel className="mr-1 h-3 w-3" />
                      Inactive
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  Created: {new Date(challenge.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="flex flex-col items-center space-y-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(challenge);
                  }}
                  className="p-1 text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                  title="Edit Challenge"
                >
                  <MdEdit className="h-5 w-5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(challenge);
                  }}
                  className="p-1 text-gray-600 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                  title="Delete Challenge"
                >
                  <MdDelete className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      />
    </Card>
  );
};

export default ChallengesTable;
